import { create, v, w } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/widgets/middleware/theme';
import { RenderResult } from '@dojo/framework/core/interfaces';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WNodeFactory, WidgetProperties, DNode } from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';

import { AccordionPane } from './accordion-pane';
import { VariantTitle } from '../interfaces';

import css from '../css/accordion2.m.css';

export interface AccordionProperties {
	/** QA key */
	xqa?: string;
	/** If true, only one TitlePane can be opened at a given time */
	exclusive?: boolean;
	/** Set the heading level of the TitlePanes */
	headingLevel?: number;
	/** Inform the widget it is part of a symantic list */
	role?: string;
	/** Initial keys for open panes */
	openKeys?: string[];
	/** Called when open panes changes */
	onOpenChanged?: (open: Set<string> | undefined) => void;
	/** boolean whether to wrap pane title/content in a div */
	useContainer?: boolean;
}

interface AccordionICache {
	openIndexes: Set<string>;
	initialOpen: boolean;
}

export interface AccordionChild {
	key?: string | number;
	title: RenderResult | VariantTitle;
	content: RenderResult;
}

const icache = createICacheMiddleware<AccordionICache>();

const factory = create({
	icache,
	theme
})
	.properties<AccordionProperties>()
	.children<AccordionChild[]>();

export const Accordion = factory(({ middleware: { icache, theme }, properties, children }) => {
	const {
		exclusive,
		headingLevel,
		role,
		openKeys = [],
		onOpenChanged,
		xqa = 'accordion',
		useContainer = true
	} = properties();
	const themeCss = theme.classes(css);

	const openIndexes = icache.getOrSet(
		'openIndexes',
		() => new Set<string>(exclusive && openKeys.length > 0 ? [openKeys[0]] : openKeys)
	);

	const initialOpen = icache.getOrSet('initialOpen', true);

	const onOpen = (index: string) => {
		if (exclusive) {
			icache.set('openIndexes', new Set([index]));
		} else {
			const openIndexes = icache.getOrSet('openIndexes', () => new Set<string>());
			openIndexes.add(index);
			icache.set('openIndexes', openIndexes);
		}
		onOpenChanged && onOpenChanged(icache.get('openIndexes'));
	};

	const onClose = (index: string) => {
		openIndexes.delete(index);
		icache.set('openIndexes', openIndexes);
		onOpenChanged && onOpenChanged(openIndexes);
	};

	const kids = children().map((kid, index): DNode => {
		const key = kid.key ? `${kid.key}` : `pane-${index}`;
		if (openIndexes.size === 0 && index === 0 && initialOpen) {
			openIndexes.add(key);
			icache.set('initialOpen', false);
		} else if (openIndexes.size > 0 && initialOpen) {
			icache.set('initialOpen', false);
		}
		const { title, content } = kid;
		return w(
			AccordionPane,
			{
				headingLevel,
				role: role === 'list' ? 'listitem' : null,
				key,
				useContainer,
				open: openIndexes.has(key),
				onOpen: () => {
					onOpen(key);
				},
				onClose: () => {
					onClose(key);
				}
			},
			[{ title, content }]
		);
	});

	return v('div', { xqa, role, classes: [themeCss.root] }, kids);
});

export default Accordion;
